import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCPDetailState } from './contextProvider/cpDetailProvider';

const getDaySuffix = (day) => {
  const d = parseInt(day);
  if (d >= 11 && d <= 13) return 'th';
  switch (d % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

function DateFilter(props) {
  const { t } = useTranslation('common');
  const { expandedYears, setExpandedYears, expandedMonths, setExpandedMonths, setColumnsFilterData, columnsFilterData } = useCPDetailState();
  const { filteredData, disableFilterSection, isBeforeAfterBetween, columnId } = props;
  const monthMap = useMemo(
    () => ({
      january: 'Jan',
      february: 'Feb',
      march: 'Mar',
      april: 'Apr',
      may: 'May',
      june: 'Jun',
      july: 'Jul',
      august: 'Aug',
      september: 'Sep',
      october: 'Oct',
      november: 'Nov',
      december: 'Dec',
    }),
    [],
  );
  const newFilters = useMemo(() => columnsFilterData?.find((item) => item.columnId === columnId), [JSON.stringify(columnsFilterData), columnId]);
  const selectedDaysSet = useMemo(() => new Set(newFilters?.selectedDays || []), [newFilters?.selectedDays]);

  const isDaySelected = useCallback(
    (year, month, day) => {
      return selectedDaysSet.has(`${day.padStart(2, '0')}-${monthMap[month.toLowerCase()]}-${year}`);
    },
    [selectedDaysSet, monthMap],
  );

  const isAllDaysSelected = useCallback(
    (year, month, days) => {
      const prefix = `${monthMap[month.toLowerCase()]}-${year}`;
      let count = 0;
      days.forEach((day) => {
        if (selectedDaysSet.has(`${day.padStart(2, '0')}-${prefix}`)) count++;
      });
      return count === days.length;
    },
    [selectedDaysSet, monthMap],
  );

  const updateSelectedDays = useCallback(
    (updatedSelectedDays) => {
      if (!newFilters) return;
      const updatedFilter = { ...newFilters, selectedDays: updatedSelectedDays };
      if (columnsFilterData?.length > 1) {
        setColumnsFilterData((prev) => prev.map((obj) => (obj.columnId === updatedFilter.columnId ? updatedFilter : obj)));
      } else {
        setColumnsFilterData([updatedFilter]);
      }
    },
    [newFilters, columnsFilterData, setColumnsFilterData],
  );

  const toggleDay = useCallback(
    (year, month, day) => {
      if (!newFilters) return;
      const key = `${day.padStart(2, '0')}-${monthMap[month.toLowerCase()]}-${year}`;
      const current = newFilters.selectedDays || [];
      let updated;
      if (selectedDaysSet.has(key)) {
        updated = current.filter((d) => d !== key);
      } else {
        updated = [...current, key];
      }
      updateSelectedDays(updated);
    },
    [newFilters, monthMap, selectedDaysSet, updateSelectedDays],
  );

  const selectAllDaysInMonth = useCallback(
    (year, month, days) => {
      if (!newFilters) return;
      const prefix = `${monthMap[month.toLowerCase()]}-${year}`;
      const current = new Set(newFilters.selectedDays || []);
      const allKeys = days.map((day) => `${day.padStart(2, '0')}-${prefix}`);
      const allSelected = allKeys.every((key) => current.has(key));
      let updated;
      if (allSelected) {
        updated = (newFilters.selectedDays || []).filter((item) => !allKeys.includes(item));
      } else {
        const set = new Set([...(newFilters.selectedDays || []), ...allKeys]);
        updated = Array.from(set);
      }
      updateSelectedDays(updated);
    },
    [newFilters, monthMap, updateSelectedDays],
  );

  const selectAllDaysInYear = useCallback(
    (year, months) => {
      if (!newFilters) return;
      const allKeys = [];
      months.forEach(({ month, days }) => {
        allKeys.push(...days.map((day) => `${day.padStart(2, '0')}-${monthMap[month.toLowerCase()]}-${year}`));
      });
      const current = new Set(newFilters.selectedDays || []);
      const allSelected = allKeys.every((key) => current.has(key));
      let updated;
      if (allSelected) {
        updated = (newFilters.selectedDays || []).filter((item) => !allKeys.includes(item));
      } else {
        const set = new Set([...(newFilters.selectedDays || []), ...allKeys]);
        updated = Array.from(set);
      }
      updateSelectedDays(updated);
    },
    [newFilters, monthMap, updateSelectedDays],
  );

  const isAllDaysInYearSelected = useCallback(
    (year, months) => {
      const allKeys = [];
      months.forEach(({ month, days }) => {
        allKeys.push(...days.map((day) => `${day.padStart(2, '0')}-${monthMap[month.toLowerCase()]}-${year}`));
      });
      return allKeys.every((key) => selectedDaysSet.has(key));
    },
    [selectedDaysSet, monthMap],
  );

  const selectAllYears = (data) => {
    const fromPreSelection = [];
    const allSelectedDaysFromState = [];
    data.forEach(({ year, months }) =>
      months.forEach(({ month, days }) => {
        const allSelectedDates = days?.map((day) => `${day.padStart(2, '0')}-${monthMap[month.toLowerCase()]}-${year}`);
        fromPreSelection.push(...(newFilters?.selectedDays?.filter((item) => allSelectedDates?.includes(item)) || []));
        allSelectedDaysFromState.push(...allSelectedDates);
      }),
    );
    const allSelected = allSelectedDaysFromState?.length === fromPreSelection?.length;
    const allSelectedDays = [];
    data.reduce((acc, { year, months }) => {
      months.forEach(({ month, days }) => {
        if (!allSelected) {
          allSelectedDays.push(...(days?.map((day) => `${day.padStart(2, '0')}-${monthMap[month.toLowerCase()]}-${year}`) || []));
        }
      });
      return acc;
    }, {});
    newFilters.selectedDays = allSelectedDays;
    setColumnsFilterData((prev) => {
      const existingIndex = prev.findIndex((f) => f.id === newFilters.id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...newFilters };
        return updated;
      }
      return [...prev, newFilters];
    });
  };

  const isAllDaysInAllYearsSelected = (data) => {
    const allselectedDays = [];
    data?.forEach((item) => {
      item?.months?.forEach((months) => {
        months?.days?.forEach((day) => {
          allselectedDays.push(`${day.padStart(2, '0')}-${monthMap[months?.month.toLowerCase()]}-${item.year}`);
        });
      });
    });
    return allselectedDays?.length === newFilters?.selectedDays?.length;
  };
  const toggleYearExpanded = (year) => {
    setExpandedYears((prev) => {
      const newSet = new Set(prev);
      newSet.has(year) ? newSet.delete(year) : newSet.add(year);
      return newSet;
    });
  };

  const toggleMonthExpanded = (year, month) => {
    setExpandedMonths((prev) => {
      const yearSet = new Set(prev[year] || []);
      yearSet.has(month) ? yearSet.delete(month) : yearSet.add(month);
      return { ...prev, [year]: yearSet };
    });
  };

  return (
    <div className="filter-panel">
      {/* <div className="common-checkbox size-xsm mb-8" style={{ pointerEvents: isBeforeAfterBetween ? "none" : "auto", opacity: isBeforeAfterBetween ? 0.5 : 1 }}> */}
      <div className="common-checkbox size-xsm mb-8" style={{ pointerEvents: 'auto', opacity: 1 }}>
        <input
          id="all_selected"
          // disabled={isBeforeAfterBetween || newFilters?.filterApplied}
          disabled={newFilters?.filterApplied}
          type="checkbox"
          checked={isAllDaysInAllYearsSelected(filteredData)}
          onChange={() => selectAllYears(filteredData)}
        />
        <label htmlFor="all_selected" className="chklabel">
          <span>{t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.SELECT_ALL')}</span>
        </label>
      </div>
      {filteredData.map(({ year, months }) => (
        <div key={year} className="year-section">
          <div className="year-header">
            <div
              className="common-checkbox size-xsm"
              // style={{ pointerEvents: isBeforeAfterBetween ? "none" : "auto", opacity: isBeforeAfterBetween ? 0.5 : 1 }}
              style={{ pointerEvents: 'auto', opacity: 1 }}
            >
              <input
                id={`year_collapse_${year}`}
                // disabled={isBeforeAfterBetween || newFilters?.filterApplied}
                disabled={newFilters?.filterApplied}
                type="checkbox"
                checked={isAllDaysInYearSelected(year, months)}
                onChange={() => selectAllDaysInYear(year, months)}
              />
              <label htmlFor={`year_collapse_${year}`} className="chklabel">
                <span>{year}</span>
              </label>
            </div>

            <button className="btn-collapse" type="button" onClick={() => toggleYearExpanded(year)}>
              {expandedYears.has(year) ? <span className="icon-new-down-arrow-icon icon-inverse f-14" /> : <span className="icon-new-down-arrow-icon f-14 " />}
            </button>
          </div>
          {expandedYears.has(year) && (
            <div className="months">
              {months.map(({ month, days }) => (
                <div key={month} className="month-section">
                  <label className="month-header">
                    <div
                      className="common-checkbox size-xsm"
                      // style={{ pointerEvents: isBeforeAfterBetween ? "none" : "auto", opacity: isBeforeAfterBetween ? 0.5 : 1 }}
                      style={{ pointerEvents: 'auto', opacity: 1 }}
                    >
                      <input
                        id={`month_collapse_${year}${month}`}
                        // disabled={isBeforeAfterBetween || newFilters?.filterApplied}
                        disabled={newFilters?.filterApplied}
                        type="checkbox"
                        checked={isAllDaysSelected(year, month, days)}
                        onChange={() => selectAllDaysInMonth(year, month, days)}
                      />
                      <label htmlFor={`month_collapse_${year}${month}`} className="chklabel">
                        <span>{month}</span>
                      </label>
                    </div>

                    <button className="btn-collapse" type="button" onClick={() => toggleMonthExpanded(year, month)}>
                      {expandedMonths[year]?.has(month) ? (
                        <span className="icon-new-down-arrow-icon icon-inverse f-14" />
                      ) : (
                        <span className="icon-new-down-arrow-icon f-14" />
                      )}
                    </button>
                  </label>
                  {expandedMonths[year]?.has(month) && (
                    <div className="days-grid">
                      {days.map((day) => (
                        <label key={day} className="day">
                          <div className="common-checkbox size-xsm">
                            <input
                              id={`day_${year}${month}${day}`}
                              type="checkbox"
                              disabled={newFilters?.filterApplied || (!isDaySelected(year, month, day) && disableFilterSection)}
                              checked={isDaySelected(year, month, day)}
                              onChange={() => toggleDay(year, month, day)}
                            />
                            <label htmlFor={`day_${year}${month}${day}`} className="chklabel">
                              <span>
                                {parseInt(day, 10)}
                                <sup>{getDaySuffix(day)}</sup>
                              </span>
                            </label>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default DateFilter;
